// iter2 — student "My Progress": term totals, Reviewers/Pitches funnels, badge tracks, and the
// attendance section. Built to the canonical design (design repo:
// docs/designs/2026-07-29-student-progress-v2/, Claude Design "My Productivity" at the Jul-29
// revision — renamed to "My Progress"). Self-scoped by default; AE-and-above may pass
// `studentEmail` (from ?student=) to view another student's numbers.
//
// Attendance has no data source (it lives in rrid-zoom-attendance), so every attendance figure
// and the 4-week log render as explicit "Not tracked yet" placeholders rather than fake numbers.
// The design's log caption is also deliberately NOT reproduced — see the QA note.
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { BadgeProgress, BadgeTrack, MyProductivity as MyProductivityPayload } from "@rrid/shared-types";
import { api } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { Badge, Card, CardBody, CardHeader, Input, Spinner } from "../../components/ui.js";

const fmtDate = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

export function MyProductivity({ studentEmail }: { studentEmail?: string } = {}): JSX.Element {
  // Default = all-time ("Term totals"); setting `from` narrows proposals/greenlits to the period.
  const [from, setFrom] = useState("");
  const [to, setTo] = useState(() => fmtDate(Date.now()));
  const weeks =
    from && to ? Math.max(0.5, Math.round(((Date.parse(to) - Date.parse(from)) / (7 * 864e5)) * 10) / 10) : null;
  const student = studentEmail?.trim() || "";

  const { data, isLoading } = useQuery({
    queryKey: qk.myProductivity(from, to, student),
    queryFn: () => {
      const q = new URLSearchParams({ ...(from ? { from } : {}), to });
      if (student) q.set("student", student);
      return api.get<MyProductivityPayload>(`/dashboard/my-productivity?${q}`);
    },
  });

  if (isLoading || !data) return <Spinner label="Loading productivity…" />;

  const earned = data.badgeTracks.flatMap((t) => t.badges).filter((b) => b.complete);
  const firstName = data.student.name?.split(" ")[0];
  const viewingOther = !!data.viewingOther || !!student;

  return (
    <div className="space-y-5">
      {viewingOther && (
        <Link to="/my-productivity" className="inline-block text-sm font-medium text-brand hover:underline">
          ← Back to Productivity
        </Link>
      )}
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">
            {viewingOther
              ? `${data.student.name ?? data.student.email}'s Progress`
              : firstName
                ? `${firstName}'s Progress`
                : "My Progress"}{" "}
            👋
          </h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {viewingOther
              ? "Their reviewer-selection work at a glance."
              : "Your reviewer-selection work at a glance."}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <Badge tone="blue">Student</Badge>
            {data.student.team && <Badge tone="slate">Team {data.student.team}</Badge>}
            {data.student.domains.length > 0 && <Badge tone="slate">{data.student.domains.join(", ")}</Badge>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-600">Period</span>
          <Input type="date" className="w-36 py-1" value={from} onChange={(e) => setFrom(e.target.value)} />
          <span className="text-slate-400">–</span>
          <Input type="date" className="w-36 py-1" value={to} onChange={(e) => setTo(e.target.value)} />
          <Badge tone="slate">{weeks ? `${weeks} weeks` : "All time"}</Badge>
        </div>
      </header>

      <TermTotals data={data} earned={earned} />

      <div className="grid gap-3 lg:grid-cols-2">
        <ReviewersFunnel data={data} />
        <PitchesFunnel data={data} />
      </div>

      <section id="badges" className="space-y-2">
        <h2 className="text-lg font-semibold">🏅 Badges</h2>
        <div className="grid items-start gap-3 md:grid-cols-2 xl:grid-cols-3">
          {data.badgeTracks.map((t) => (
            <TrackCard key={t.name} track={t} />
          ))}
        </div>
        <p className="text-xs text-slate-400">
          ○ = not started · ◌ = tracked outside the webapp (quizzes &amp; attendance) — those milestones flip once their
          source is connected.
        </p>
      </section>

      <AttendanceLog attendance={data.attendance} />
    </div>
  );
}

/**
 * 4-week attendance log (WO-7/30 Track D [43:52–46:12]) — one card per week showing whether the
 * student made their weekly team meeting, and which one, plus the global workshop.
 *
 * The webapp only CONSUMES attendance; ingestion is the attendance-dashboard thread's. Until that
 * pipeline lands the API returns `source: "none"` and this renders the gap rather than an empty
 * grid, which would read as "you attended nothing".
 *
 * Copy is bound by Noah's locked rules (design repo
 * docs/designs/2026-07-24-attendance-dashboard/attendance-rules.md): a STUDENT sees attended /
 * missed only — never minute thresholds, durations, or "need 30m/60m". The Jul-29 design's
 * caption ("Editorial (>1 hour)") is deliberately not reproduced; those rules also put Editorial
 * out of scope, so only the team meeting and the global workshop appear here.
 */
function AttendanceLog({ attendance }: { attendance: MyProductivityPayload["attendance"] }): JSX.Element {
  const weeks = attendance.weeks.slice(0, 4);
  return (
    <section id="attendance-log" className="space-y-2">
      <h2 className="text-lg font-semibold">📅 4-week attendance log</h2>
      {attendance.source === "none" || weeks.length === 0 ? (
        <Card>
          <CardBody className="space-y-1.5">
            <p className="text-sm text-slate-600">Attendance isn&apos;t being tracked in the webapp yet.</p>
            <p className="text-xs text-slate-400">
              Your weekly team meeting and global workshop marks will appear here once Zoom attendance is connected.
              Until then the attendance totals above read “—” rather than a number we can&apos;t stand behind.
            </p>
          </CardBody>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {weeks.map((w) => (
            <Card key={w.weekStart}>
              <CardHeader>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Week of {new Date(`${w.weekStart}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </h3>
              </CardHeader>
              <CardBody className="space-y-2">
                <AttendanceMark
                  label={w.teamMeeting?.title ?? "Weekly team meeting"}
                  state={w.teamMeeting ? (w.teamMeeting.attended ? "yes" : "no") : "none"}
                />
                <AttendanceMark
                  label="Global workshop"
                  state={w.globalWorkshop ? (w.globalWorkshop.attended ? "yes" : "no") : "none"}
                />
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}

function AttendanceMark({ label, state }: { label: string; state: "yes" | "no" | "none" }): JSX.Element {
  const mark = state === "yes" ? "✓" : state === "no" ? "✕" : "–";
  const tone =
    state === "yes" ? "text-green-700" : state === "no" ? "text-red-700" : "text-slate-300";
  return (
    <div className="flex items-start gap-2">
      <span className={`shrink-0 text-sm font-bold ${tone}`} aria-hidden>
        {mark}
      </span>
      <span className="min-w-0 flex-1 text-xs text-slate-600">{label}</span>
      <span className="sr-only">{state === "yes" ? "attended" : state === "no" ? "missed" : "none expected"}</span>
    </div>
  );
}

// ── Term totals ──────────────────────────────────────────────────────────────
function TermTotals({ data, earned }: { data: MyProductivityPayload; earned: BadgeProgress[] }): JSX.Element {
  const t = data.totals;
  return (
    <Card>
      <CardHeader>
        <h3 className="text-sm font-semibold">Term totals</h3>
      </CardHeader>
      {/* Jul-29 revision: 6 → 7 columns, headers abbreviated so they fit. */}
      <CardBody className="grid grid-cols-2 gap-y-4 divide-slate-100 sm:grid-cols-4 xl:grid-cols-7 xl:divide-x">
        <Stat label="Pitches" color="text-blue-700" value={t.pitchesTeam + t.pitchesEditorial}>
          <span className="flex gap-1">
            <Badge tone="blue">T {t.pitchesTeam}</Badge>
            <Badge tone="amber">E {t.pitchesEditorial}</Badge>
          </span>
        </Stat>
        <Stat label="Proposals" color="text-violet-700" value={t.proposals} sub="Truly proposed" />
        <Stat
          label="Greenlits to date"
          color="text-green-700"
          value={t.greenlits}
          sub={`${t.greenlitRate}% rate (of QCd proposals)`}
        />
        {/* Attendance has no data source yet (design parity — placeholders, not fake numbers). */}
        <Stat label="Team atd." value="—" sub="Not tracked yet" />
        <Stat label="Editorial atd." value="—" sub="Not tracked yet" />
        <Stat label="Class atd." value="—" sub="Not tracked yet" />
        <Stat label="Office hours atd." value="—" sub="Not tracked yet" />
      </CardBody>
      <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-4 py-2.5">
        <span className="text-xs font-medium text-slate-600">Badges earned ({earned.length}):</span>
        {earned.length === 0 ? (
          <span className="text-xs text-slate-400">None yet — see the tracks below.</span>
        ) : (
          earned.map((b) => (
            <Badge key={b.key} tone="green">
              {b.name}
            </Badge>
          ))
        )}
        <a href="#badges" className="ml-auto text-xs font-medium text-brand hover:underline">
          See all badges ↓
        </a>
        <a href="#attendance-log" className="text-xs font-medium text-brand hover:underline">
          Attendance details ↓
        </a>
      </div>
    </Card>
  );
}

function Stat({
  label,
  value,
  sub,
  color = "text-slate-800",
  children,
}: {
  label: string;
  value: string | number;
  sub?: string;
  color?: string;
  children?: JSX.Element;
}): JSX.Element {
  return (
    <div className="space-y-1 px-4 first:pl-0">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-3xl font-bold leading-tight ${color}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
      {children}
    </div>
  );
}

// ── Funnels ──────────────────────────────────────────────────────────────────
function FunnelRow({
  label,
  pill,
  pillTone,
  count,
  max,
  bar,
}: {
  label: string;
  pill?: string;
  pillTone?: "blue" | "green" | "red" | "amber" | "slate";
  count: number;
  max: number;
  bar: string;
}): JSX.Element {
  const width = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="flex items-center gap-1.5">
          <span className="font-medium text-slate-700">{label}</span>
          {pill && <Badge tone={pillTone ?? "slate"}>{pill}</Badge>}
        </span>
        <span className="font-semibold text-slate-500">{count}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full rounded-full ${bar}`} style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

function ReviewersFunnel({ data }: { data: MyProductivityPayload }): JSX.Element {
  const c = data.reviewerStatusCounts;
  const max = data.reviewerProposedTotal;
  const n = (k: keyof typeof c): number => c[k] ?? 0;
  return (
    <Card className="h-full">
      <CardHeader className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Reviewers</h3>
        <span className="text-xs text-slate-400">Student Reviewer Inputs · status</span>
      </CardHeader>
      <CardBody className="space-y-3.5">
        <FunnelRow label="Reviewers proposed" pill="counts as proposed" pillTone="blue" count={max} max={max} bar="bg-blue-600" />
        <FunnelRow label="Greenlit" pill="Invite" pillTone="green" count={n("Invite")} max={max} bar="bg-green-600" />
        <FunnelRow label="AE reject" pill="Reject" pillTone="red" count={n("Reject")} max={max} bar="bg-red-600" />
        <FunnelRow label="Fixables" pill="Fixable" pillTone="amber" count={n("Fixable")} max={max} bar="bg-amber-500" />
        {/* Jul-29 revision: proposals still awaiting an AE decision get their own row, between
            Fixables and Student reject. */}
        <FunnelRow label="Unchecked" pill="No Status" pillTone="slate" count={n("Unchecked")} max={max} bar="bg-slate-300" />
        <FunnelRow label="Student reject" pill="Student Reject" pillTone="slate" count={n("Student Reject")} max={max} bar="bg-slate-400" />
        {n("COI Detected") > 0 && (
          <FunnelRow label="COI detected" pill="COI Detected" pillTone="red" count={n("COI Detected")} max={max} bar="bg-slate-400" />
        )}
        <p className="border-t border-slate-100 pt-2.5 text-xs text-slate-400">
          Student Reject and COI Detected don't count as proposed — they leave the greenlit-rate denominator.
        </p>
      </CardBody>
    </Card>
  );
}

function PitchesFunnel({ data }: { data: MyProductivityPayload }): JSX.Element {
  const f = data.pitchFunnel;
  const max = Math.max(f.claimed, f.toPitchTeam, f.toPitchEditorial, f.selected);
  return (
    <Card className="h-full">
      <CardHeader className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Pitches</h3>
        <span className="text-xs text-slate-400">Selected · status</span>
      </CardHeader>
      <CardBody className="space-y-3.5">
        <FunnelRow label="Claimed (Taken)" count={f.claimed} max={max} bar="bg-blue-600" />
        <FunnelRow label="To Pitch(Team)" count={f.toPitchTeam} max={max} bar="bg-blue-400" />
        <FunnelRow label="To Pitch(Editorial)" count={f.toPitchEditorial} max={max} bar="bg-amber-500" />
        <FunnelRow label="Selected" count={f.selected} max={max} bar="bg-green-600" />
        <p className="border-t border-slate-100 pt-2.5 text-xs text-slate-400">
          Claimed → pitched at a team or editorial meeting → Selected into the reviewer pipeline.
        </p>
      </CardBody>
    </Card>
  );
}

// ── Badges ───────────────────────────────────────────────────────────────────
function TrackCard({ track }: { track: BadgeTrack }): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <h3 className="text-sm font-semibold">{track.name}</h3>
      </CardHeader>
      <div className="px-4">
        {track.badges.map((b, i) => (
          <BadgeRow key={b.key} badge={b} last={i === track.badges.length - 1} />
        ))}
      </div>
    </Card>
  );
}

function BadgeRow({ badge, last }: { badge: BadgeProgress; last: boolean }): JSX.Element {
  const pct = badge.total > 0 ? Math.round((badge.done / badge.total) * 100) : 0;
  return (
    <div className={`flex gap-3 py-3 ${last ? "" : "border-b border-slate-100"}`}>
      <img
        src={`/badges/badge-${badge.key}.png`}
        alt={badge.name}
        className={`h-12 w-12 flex-none rounded-full object-cover ${badge.complete ? "" : "opacity-60 grayscale-[35%]"}`}
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[13px] font-semibold text-slate-900">{badge.name}</span>
          <Badge tone={badge.complete ? "green" : "slate"}>{badge.complete ? "Complete" : "To Do"}</Badge>
        </div>
        <div className="mt-1.5 flex items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-brand" style={{ width: `${pct}%` }} />
          </div>
          <span className="whitespace-nowrap text-[11px] font-semibold text-slate-500">
            {badge.done} / {badge.total}
          </span>
        </div>
        <div className="mt-1.5 space-y-0.5">
          {badge.milestones.map((m) => (
            <div key={m.label} className="flex items-baseline gap-1.5 text-xs text-slate-500">
              <span
                className={`font-bold ${m.done ? "text-green-700" : "text-slate-400"}`}
                title={m.tracked ? undefined : "Tracked outside the webapp (quiz / attendance)"}
              >
                {m.done ? "✓" : m.tracked ? "○" : "◌"}
              </span>
              <span className={m.tracked ? "" : "italic"}>{m.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
